import React, { useState } from "react";
import ErrorDisplay from "../common/ErrorDisplay";
import { Problem } from "../api/backendApi";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  VisCountries as VisCountryStrings,
  VisCountry,
  VisFinnishVehicleTypes,
  VisSwissNumberPlateColors,
  VisAmericanStates,
  GetNumberPlateVehiclesRequest,
  visAPI,
  VisNumberPlateVehicleModel,
  VisAustralianStates,
} from "../api/visApi";
import { useRoutablePages } from "../common/RoutablePages";

function NumberPlateRequestPage() {
  const [error, setError] = useState<Problem | string>();
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState<VisCountry>(VisCountry.Netherlands);
  const [numberPlate, setNumberPlate] = useState<string>("");
  const [usaState, setUsaState] = useState<string>("AL");
  const [ausState, setAusState] = useState<string>("ACT");
  const [swissNumberPlateColor, setSwissNumberPlateColor] = useState(0);
  const [finnishVehicleType, setFinnishVehicleType] = useState(0);
  const [vehicles, setVehicles] = useState<VisNumberPlateVehicleModel[]>();
  const [warning, setWarning] = useState<string>();
  const routes = useRoutablePages();

  const handleRequest = async () => {
    const request: GetNumberPlateVehiclesRequest = {
      numberPlate: numberPlate.trim(),
      country: country,
      usaState: country === VisCountry.UnitedStates ? usaState : undefined,
      swissNumberPlateColor:
        country === VisCountry.Switzerland ? swissNumberPlateColor : undefined,
      finnishVehicleType:
        country === VisCountry.Finland ? finnishVehicleType : undefined,
      ausState: country === VisCountry.Australia ? ausState : undefined,
    };

    setLoading(true);
    var response = await visAPI.GetNumberPlate(request);
    setLoading(false);

    if (response.ok) {
      setError(undefined);
      setVehicles(response.value.vehicles);
      setWarning(response.value.warning);
    } else {
      setError(response.problem);
      setVehicles(undefined);
      setWarning(undefined);
    }
  };

  return (
    <>
      <div className="main-container-title">
        <h1>Number plate requests</h1>
        <ErrorDisplay error={error} />
      </div>
      <div className="main-container-content">
        <Card style={{ maxWidth: 700 }}>
          <Card.Body>
            <Container>
              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label>Country</Form.Label>
                    <Form.Select
                      defaultValue={VisCountry.Netherlands}
                      onChange={(e) =>
                        setCountry(Number(e.currentTarget.value))
                      }
                    >
                      {Object.keys(VisCountryStrings).map((k) => (
                        <option key={k} value={k}>
                          {VisCountryStrings[Number(k) as VisCountry]}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  {country === VisCountry.Finland && (
                    <Form.Group>
                      <Form.Label>Vehicle type</Form.Label>
                      <Form.Select
                        defaultValue={0}
                        onChange={(e) =>
                          setFinnishVehicleType(Number(e.currentTarget.value))
                        }
                      >
                        {Object.keys(VisFinnishVehicleTypes).map((k) => (
                          <option key={k} value={k}>
                            {VisFinnishVehicleTypes[Number(k)]}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}
                  {country === VisCountry.Switzerland && (
                    <Form.Group>
                      <Form.Label>Number plate color</Form.Label>
                      <Form.Select
                        defaultValue={0}
                        onChange={(e) =>
                          setSwissNumberPlateColor(
                            Number(e.currentTarget.value),
                          )
                        }
                      >
                        {Object.keys(VisSwissNumberPlateColors).map((k) => (
                          <option key={k} value={k}>
                            {VisSwissNumberPlateColors[Number(k)]}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}
                  {country === VisCountry.UnitedStates && (
                    <Form.Group>
                      <Form.Label>State</Form.Label>
                      <Form.Select
                        defaultValue={VisAmericanStates.AL}
                        onChange={(e) => setUsaState(e.currentTarget.value)}
                      >
                        {Object.keys(VisAmericanStates).map((k) => (
                          <option key={k} value={k}>
                            {VisAmericanStates[k]}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}
                  {country === VisCountry.Australia && (
                    <Form.Group>
                      <Form.Label>State</Form.Label>
                      <Form.Select
                        defaultValue={VisAustralianStates.ACT}
                        onChange={(e) => setAusState(e.currentTarget.value)}
                      >
                        {Object.keys(VisAustralianStates).map((k) => (
                          <option key={k} value={k}>
                            {VisAustralianStates[k]}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label>Number plate</Form.Label>
                    <Form.Control
                      defaultValue={""}
                      maxLength={20}
                      onChange={(e) =>
                        setNumberPlate(e.currentTarget.value.trim())
                      }
                    />
                  </Form.Group>
                </Col>
                <Col></Col>
              </Row>
            </Container>
          </Card.Body>

          <Card.Footer>
            <Button disabled={loading} onClick={() => handleRequest()}>
              Find Vehicle {loading && <Spinner size="sm" />}
            </Button>
          </Card.Footer>
        </Card>
        {vehicles && (
          <Card className="my-3">
            <Card.Body>
              {warning && <Alert variant="warning">{warning}</Alert>}
              <Table>
                <thead>
                  <tr>
                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Rec ID</th>
                    <th style={{ width: 1, whiteSpace: "nowrap" }}>
                      Vehicle File
                    </th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.length === 0 && (
                    <tr>
                      <td colSpan={3}>No vehicles found...</td>
                    </tr>
                  )}
                  {vehicles.map((v) => (
                    <tr key={v.recId}>
                      <td style={{ width: 1, whiteSpace: "nowrap" }}>
                        {v.recId}
                      </td>
                      <td style={{ width: 1, whiteSpace: "nowrap" }}>
                        <a
                          href={routes.getVehicleFileDetails(
                            v.vehicleFileNumber,
                          )}
                        >
                          {v.vehicleFileNumber}
                        </a>
                      </td>
                      <td>{v.description}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </div>
    </>
  );
}

export default NumberPlateRequestPage;
